(() => {
  'use strict';

  function createCartClient({ fetch: request, root, sectionIds, sectionsUrl }) {
    const base = root.endsWith('/') ? root : `${root}/`;
    const ids = [...new Set(sectionIds)].slice(0, 5);
    const options = { credentials: 'same-origin', headers: { Accept: 'application/json' } };
    const validSections = (sections) => ids.every((id) => typeof sections?.[id] === 'string' && sections[id].trim());
    async function readCart() {
      const response = await request(`${base}cart.js`, { ...options, cache: 'no-store' });
      if (!response.ok) throw new Error('cart-read');
      const data = await response.json();
      if (!Number.isInteger(data.item_count) || !Array.isArray(data.items)) throw new Error('cart-shape');
      return data;
    }
    async function readSections() {
      if (!ids.length) return {};
      const separator = sectionsUrl.includes('?') ? '&' : '?';
      const response = await request(`${sectionsUrl}${separator}sections=${encodeURIComponent(ids.join(','))}`, { ...options, headers: { Accept: 'text/html' }, cache: 'no-store' });
      if (!response.ok) throw new Error('sections-read');
      return response.json();
    }
    async function resetDeliverySelection() {
      const attributes = {
        globe_DeliveryMethod: '', globe_PickupPointId: '', globe_PickupPointName: '',
        globe_PickupPointAddress: '', globe_PickupPointData: '', globe_PickupPointCustomerId: '',
      };
      const response = await request(`${base}cart/update.js`, {
        ...options, method: 'POST', headers: { ...options.headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
      });
      if (!response.ok) throw new Error('delivery-reset');
      const cart = await response.json();
      if (!cart.attributes || Object.keys(attributes).some((key) => cart.attributes[key])) throw new Error('delivery-reset');
      return cart;
    }
    async function mutate(action, payload) {
      if (!['add', 'change'].includes(action)) throw new Error('invalid-action');
      if (!Number.isInteger(payload.quantity) || payload.quantity < (action === 'add' ? 1 : 0) || !payload.id) throw new Error('invalid-payload');
      let data = null;
      let ok = false;
      let uncertain = false;
      let error = '';
      try {
        const response = await request(`${base}cart/${action}.js`, {
          ...options,
          method: 'POST',
          headers: { ...options.headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, sections: ids, sections_url: sectionsUrl }),
        });
        data = await response.json();
        ok = response.ok && !(Number(data.status) >= 400);
        if (!ok) error = typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : '';
      } catch {
        uncertain = true;
      }
      let cart = await readCart().catch(() => null);
      let sections = data?.sections;
      if (!ok || !validSections(sections)) sections = await readSections().catch(() => sections);
      return { ok, uncertain, error, cart, sections, rendered: validSections(sections) };
    }
    return { mutate, readCart, readSections, resetDeliverySelection, validSections };
  }

  function isDeliverySelectionReady(attributes) {
    const method = attributes?.globe_DeliveryMethod;
    return method === 'home' || (method === 'pickup' && typeof attributes?.globe_PickupPointId === 'string' && attributes.globe_PickupPointId.trim() !== '');
  }

  if (typeof document === 'undefined') {
    globalThis.VezeloCartProtocol = { createCartClient, isDeliverySelectionReady };
    return;
  }
  if (window.vezeloCommerceLoaded) return;
  window.vezeloCommerceLoaded = true;

  const getConfig = () => {
    try { return JSON.parse(document.querySelector('[data-commerce-config]')?.textContent || '{}'); }
    catch { return {}; }
  };
  const getRegions = () => [...document.querySelectorAll('[data-cart-region]')];
  const sectionContext = () => {
    const url = new URL(window.location.href);
    ['sections', 'section_id', 'fixture'].forEach((key) => url.searchParams.delete(key));
    return `${url.pathname}${url.search}`;
  };
  const client = () => createCartClient({
    fetch: window.fetch.bind(window),
    root: window.Shopify?.routes?.root || getConfig().root || '/',
    sectionIds: getRegions().map((region) => region.dataset.sectionId),
    sectionsUrl: sectionContext(),
  });
  let busy = false;
  let returnFocus = null;
  let pendingReturnFocus = null;
  let disabledControls = new Map();
  const drawer = () => document.querySelector('[data-cart-dialog]');
  const stickyObserver = typeof ResizeObserver === 'function' ? new ResizeObserver((entries) => {
    const height = Math.max(...entries.map((entry) => entry.target.getBoundingClientRect().height), 0);
    document.documentElement.style.setProperty('--sticky-height', `${Math.ceil(height)}px`);
  }) : null;

  function syncDeliveryButtons() {
    document.querySelectorAll('[data-cart-form]').forEach((form) => {
      const ready = form.dataset.deliveryReady === 'true';
      const button = form.querySelector('[data-delivery-checkout]');
      if (button) button.disabled = busy || !ready;
      const hint = form.querySelector('[data-delivery-hint]');
      if (hint) hint.hidden = ready;
    });
  }
  function updateDeliverySelection(cart) {
    if (!cart || !cart.attributes) return;
    const ready = String(isDeliverySelectionReady(cart.attributes));
    document.querySelectorAll('[data-cart-form]').forEach((form) => { form.dataset.deliveryReady = ready; });
    syncDeliveryButtons();
  }
  ['cart:updated', 'cart:refresh', 'cart:change', 'cart.change', 'globe:pickup-point-selected'].forEach((name) => {
    document.addEventListener(name, (event) => updateDeliverySelection(event.detail?.cart));
    window.addEventListener(name, (event) => updateDeliverySelection(event.detail?.cart));
  });

  function initialize(scope = document) {
    syncDeliveryButtons();
    scope.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      gallery.querySelectorAll('[data-gallery-slide]').forEach((slide) => { slide.hidden = !slide.classList.contains('is-active'); });
      const controls = gallery.querySelector('[data-gallery-controls]');
      if (controls) controls.hidden = false;
    });
    scope.querySelectorAll('.quantity').forEach((quantity) => {
      quantity.classList.add('is-enhanced');
      quantity.querySelectorAll('[data-quantity-step]').forEach((button) => { button.hidden = false; });
      syncQuantity(quantity);
    });
    scope.querySelectorAll('[data-cart-form]').forEach((form) => form.classList.add('is-enhanced'));
    scope.querySelectorAll('[data-sticky-purchase]').forEach((sticky) => {
      if (!sticky.dataset.observed) {
        sticky.dataset.observed = 'true';
        stickyObserver?.observe(sticky);
        if (sticky.hasAttribute('data-sticky-between-sections') && typeof IntersectionObserver === 'function') {
          const hero = document.querySelector('.vezelo-hero');
          const purchase = document.getElementById(sticky.dataset.targetForm);
          if (hero && purchase) {
            const updateVisibility = () => {
              const heroBox = hero.getBoundingClientRect();
              const purchaseBox = purchase.getBoundingClientRect();
              const purchaseVisible = purchaseBox.top < innerHeight && purchaseBox.bottom > 0;
              sticky.hidden = heroBox.bottom > 0 || purchaseVisible;
            };
            const observer = new IntersectionObserver(updateVisibility);
            observer.observe(hero);
            observer.observe(purchase);
            sticky._sectionVisibilityObserver = observer;
            updateVisibility();
          }
        }
      }
    });
    const dialog = drawer();
    if (dialog && !dialog.dataset.initialized) {
      dialog.dataset.initialized = 'true';
      dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('cart-is-open');
        const target = returnFocus?.isConnected ? returnFocus : document.querySelector('[data-cart-open]');
        if (target?.disabled) pendingReturnFocus = target;
        else { pendingReturnFocus = null; target?.focus({ preventScroll: true }); }
      });
    }
  }
  function syncQuantity(quantity) {
    const input = quantity.querySelector('input');
    if (!input) return;
    const value = Number(input.value);
    quantity.querySelectorAll('[data-quantity-step]').forEach((button) => {
      const down = Number(button.dataset.quantityStep) < 0;
      button.disabled = input.disabled || (down ? value <= Number(input.min || 1) : input.max !== '' && value >= Number(input.max));
    });
  }
  function openDrawer(trigger) {
    const dialog = drawer();
    if (!dialog?.showModal) return false;
    if (!dialog.open) {
      returnFocus = trigger || document.activeElement;
      dialog.showModal();
      document.documentElement.classList.add('cart-is-open');
      dialog.querySelector('[data-cart-close]')?.focus();
    }
    return true;
  }
  function setBusy(value, form) {
    busy = value;
    if (value) {
      disabledControls = new Map();
      document.querySelectorAll('[data-add-button], [data-sticky-submit], [data-quantity-step], [data-product-form] input[type="number"], [data-cart-form] button, [data-cart-quantity]').forEach((control) => {
        disabledControls.set(control, control.disabled);
        control.disabled = true;
      });
      getRegions().forEach((region) => region.setAttribute('aria-busy', 'true'));
    } else {
      disabledControls.forEach((disabled, control) => { if (control.isConnected) control.disabled = disabled; });
      disabledControls.clear();
      getRegions().forEach((region) => region.removeAttribute('aria-busy'));
      document.querySelectorAll('.quantity').forEach(syncQuantity);
      if (pendingReturnFocus && !drawer()?.open) {
        if (pendingReturnFocus.isConnected && (document.activeElement === document.body || drawer()?.contains(document.activeElement))) pendingReturnFocus.focus({ preventScroll: true });
        pendingReturnFocus = null;
      }
    }
    syncDeliveryButtons();
    if (form) {
      form.setAttribute('aria-busy', String(value));
      const buttons = [form.querySelector('[data-add-button]'), ...document.querySelectorAll('[data-sticky-submit]')].filter((button) => button && (button.form === form || form.contains(button)));
      buttons.forEach((button) => { button.querySelector('[data-button-label]').textContent = value ? getConfig().adding : button.dataset.idleLabel; });
    }
  }
  function updateCount(cart) {
    if (!cart) return;
    updateDeliverySelection(cart);
    document.querySelectorAll('[data-cart-count]').forEach((element) => { element.textContent = String(cart.item_count); });
  }
  function renderSections(sections) {
    const active = document.activeElement;
    const activeRegion = active?.closest('[data-cart-region]');
    const activeKey = active?.closest('[data-cart-line]')?.dataset.key;
    const wasRemove = active?.matches('[data-cart-remove]');
    let complete = true;
    getRegions().forEach((region) => {
      const html = sections?.[region.dataset.sectionId];
      if (typeof html !== 'string') { complete = false; return; }
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = [...parsed.querySelectorAll('[data-cart-region]')].find((element) => element.dataset.sectionId === region.dataset.sectionId);
      if (!replacement) { complete = false; return; }
      replacement.querySelectorAll('noscript').forEach((element) => element.remove());
      region.replaceChildren(...replacement.childNodes);
      region.dataset.itemCount = replacement.dataset.itemCount;
      initialize(region);
    });
    if (activeRegion && activeRegion.isConnected) {
      const line = [...activeRegion.querySelectorAll('[data-cart-line]')].find((element) => element.dataset.key === activeKey);
      const target = line?.querySelector(wasRemove ? '[data-cart-remove]' : '[data-cart-quantity]') || activeRegion.querySelector('a, button, input') || drawer()?.querySelector('[data-cart-close]');
      target?.focus({ preventScroll: true });
    }
    return complete;
  }
  function showError(scope, message, selector) {
    const error = scope.querySelector(selector);
    if (error) { error.textContent = message; error.hidden = !message; }
  }
  async function refreshCart() {
    if (busy) return;
    setBusy(true);
    try {
      const currentClient = client();
      const cart = await currentClient.readCart();
      const sections = await currentClient.readSections();
      updateCount(cart);
      if (!renderSections(sections) && drawer()?.open) window.location.assign(getConfig().cartUrl);
    } catch {
      if (drawer()?.open) window.location.assign(getConfig().cartUrl);
    } finally { setBusy(false); }
  }
  async function addProduct(form, trigger) {
    if (busy || !form.reportValidity()) return;
    const values = new FormData(form);
    const id = values.get('id');
    const quantity = Number(values.get('quantity') || 1);
    if (!id || !Number.isInteger(quantity) || quantity < 1) return;
    showError(form, '', '[data-product-error]');
    form.querySelector('[data-review-cart]').hidden = true;
    form.querySelector('[data-product-status]').textContent = '';
    setBusy(true, form);
    try {
      const currentClient = client();
      try {
        const cart = await currentClient.resetDeliverySelection();
        updateCount(cart);
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
      } catch {
        showError(form, getConfig().error, '[data-product-error]');
        return;
      }
      const result = await currentClient.mutate('add', { id, quantity });
      updateCount(result.cart);
      const rendered = result.rendered && renderSections(result.sections);
      if (result.ok) {
        form.querySelector('[data-product-status]').textContent = getConfig().added;
        if (!rendered || !openDrawer(trigger)) { window.location.assign(getConfig().cartUrl); return; }
        drawer().querySelector('[data-cart-announcement]').textContent = getConfig().added;
      } else {
        showError(form, result.uncertain ? getConfig().uncertain : result.error || getConfig().error, '[data-product-error]');
        form.querySelector('[data-review-cart]').hidden = false;
      }
    } catch {
      showError(form, getConfig().uncertain, '[data-product-error]');
      form.querySelector('[data-review-cart]').hidden = false;
    } finally { setBusy(false, form); }
  }
  async function changeItem(key, quantity, region) {
    if (busy || !Number.isInteger(quantity) || quantity < 0) return;
    setBusy(true);
    try {
      const result = await client().mutate('change', { id: key, quantity });
      updateCount(result.cart);
      const rendered = result.rendered && renderSections(result.sections);
      if (!rendered) { window.location.assign(getConfig().cartUrl); return; }
      if (!result.ok) showError(region, result.uncertain ? getConfig().uncertain : result.error || getConfig().error, '[data-cart-error]');
      else {
        const status = region.querySelector('[data-cart-status]') || drawer()?.querySelector('[data-cart-announcement]');
        if (status) status.textContent = getConfig().updated;
      }
    } catch { showError(region, getConfig().error, '[data-cart-error]'); }
    finally { setBusy(false); }
  }

  document.addEventListener('pointerdown', (event) => {
    const button = event.target.closest('[data-delivery-checkout]');
    const quantity = document.activeElement;
    if (event.button === 0 && button && !button.disabled && quantity?.matches('[data-cart-quantity]') && quantity.form === button.form) event.preventDefault();
  });
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form.matches('[data-product-form]')) { event.preventDefault(); addProduct(form, event.submitter); }
    else if (form.matches('[data-cart-form]')) {
      if (busy) event.preventDefault();
      else if (event.submitter?.name === 'checkout' && form.dataset.deliveryReady !== 'true') {
        event.preventDefault();
        form.querySelector('[data-delivery-hint]')?.scrollIntoView({ block: 'nearest' });
      }
    }
  });
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (input.closest('.quantity')) syncQuantity(input.closest('.quantity'));
    if (input.matches('[data-cart-quantity]') && input.reportValidity()) changeItem(input.dataset.cartKey, Number(input.value), input.closest('[data-cart-region]'));
  });
  document.addEventListener('click', (event) => {
    const target = event.target.closest('a, button, [data-cart-dialog]');
    if (!target) return;
    if (target.matches('[data-cart-open]')) {
      if (openDrawer(target)) { event.preventDefault(); refreshCart(); }
    } else if (target.matches('[data-cart-close]')) drawer()?.close();
    else if (target.matches('[data-cart-dialog]') && event.target === target) {
      const bounds = target.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) target.close();
    } else if (target.matches('[data-cart-remove]')) {
      event.preventDefault();
      changeItem(target.dataset.cartKey, 0, target.closest('[data-cart-region]'));
    } else if (target.matches('[data-quantity-step]')) {
      const input = target.closest('.quantity').querySelector('input');
      if (Number(target.dataset.quantityStep) > 0) input.stepUp(); else input.stepDown();
      input.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (target.matches('[data-gallery-prev], [data-gallery-next]')) {
      const gallery = target.closest('[data-product-gallery]');
      const links = [...gallery.querySelectorAll('[data-gallery-target]')];
      const active = links.findIndex((link) => link.hasAttribute('aria-current'));
      const direction = target.hasAttribute('data-gallery-next') ? 1 : -1;
      links[(Math.max(active, 0) + direction + links.length) % links.length]?.click();
    } else if (target.matches('[data-gallery-target]')) {
      event.preventDefault();
      const gallery = target.closest('[data-product-gallery]');
      gallery.querySelectorAll('[data-gallery-slide]').forEach((slide) => {
        const selected = slide.dataset.gallerySlide === target.dataset.galleryTarget;
        if (!selected) slide.querySelectorAll('video').forEach((video) => video.pause());
        slide.hidden = !selected;
        slide.classList.toggle('is-active', selected);
      });
      gallery.querySelectorAll('[data-gallery-target]').forEach((link) => {
        if (link === target) link.setAttribute('aria-current', 'true'); else link.removeAttribute('aria-current');
      });
    }
  });
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-sticky-purchase]').forEach((sticky) => {
      stickyObserver?.unobserve(sticky);
      sticky._sectionVisibilityObserver?.disconnect();
    });
    if (!drawer()?.open) document.documentElement.classList.remove('cart-is-open');
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshCart();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
})();
